import { z } from 'zod/v3';
import { transformNull } from '../../../common/utils/schema/transformers/transformNull';
import { transformToValidPeriod } from '../../../common/utils/schema/transformers/transformValidPeriod';
import { zIsoDate } from '../../../common/utils/schema/zIsoDate';
import { categoriesToHierarchy, mapParentCodeToParentKey } from '../../utils';
import { CategoryFormDataSchema } from './CategoryFormDataSchema';
import { EntitySchema } from '../../../common/utils/schema/EntitySchema';

/**
 * Hierarchy: Map parentCodes to parentKeys
 */
const transformCategoriesAndGroups = <
  T extends {
    categories: z.infer<typeof CategoryFormDataSchema>[];
    categoryGroups: z.infer<typeof CategoryFormDataSchema>[];
    combinedGroups: z.infer<typeof CategoryFormDataSchema>[];
  },
>(
  data: T,
) => {
  const { categories, categoryGroups, combinedGroups, ...rest } = data;
  const [mappedCategories, mappedCategoryGroups, mappedCombinedGroups] =
    mapParentCodeToParentKey(categories, categoryGroups, combinedGroups);
  return {
    ...rest,
    categories: mappedCategories,
    categoryGroups: mappedCategoryGroups,
    combinedGroups: mappedCombinedGroups,
    hierarchy: categoriesToHierarchy(mappedCategories),
  };
};

const ClassifierGroupsSchema = z.object({
  categories: z.array(CategoryFormDataSchema),
  categoryGroups: z.array(CategoryFormDataSchema),
  combinedGroups: z.array(CategoryFormDataSchema),
});

const ClassifierBaseFormDataSchema = EntitySchema.merge(
  ClassifierGroupsSchema,
).extend({
  id: z.number().nullable(),
  code: z.string(),
  name: z.string(),
  description: transformNull<string>(z.string(), ''),
  validFrom: zIsoDate()
    .nullable()
    .transform((v) => v ?? null),
  validTo: zIsoDate()
    .nullable()
    .transform((v) => v ?? null),
  inUse: transformNull(z.boolean(), false),
});

export const ClassifierFormDataSchema = z.union([
  transformToValidPeriod(
    ClassifierBaseFormDataSchema.extend({
      hasCategories: z.literal(true),
      codeFormat: transformNull(z.literal(''), '' as const),
      dataTypeId: z.literal(null),
    }),
  ).transform(transformCategoriesAndGroups),
  transformToValidPeriod(
    ClassifierBaseFormDataSchema.extend({
      hasCategories: z.literal(false),
      codeFormat: z.string().nullable(),
      dataTypeId: z.number().nullable(),
    }),
  ).transform(transformCategoriesAndGroups),
]);

export const ClassifierGroupsFormDataSchema = ClassifierGroupsSchema.transform(
  transformCategoriesAndGroups,
);
